import classNames from 'classnames';
import { StylishNewCheckbox } from 'components/DesignSystems/New/StylishNewCheckbox';
import React from 'react';
import { Controller, RegisterOptions, useFormContext } from 'react-hook-form';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

type FormCheckboxGroupFieldProps = {
  title: string;
  fieldName: string;
  options: {
    label: string;
    value: string;
    disabled?: boolean;
    tooltip?: string;
  }[];
  rules?:
    | { [key: string]: { value: unknown; message: string } }
    | RegisterOptions;
  variant?: 'full' | 'half';
  onChangeCallback?: (value: string | string[] | undefined) => void;
};

const FormCheckboxGroupField: React.FC<FormCheckboxGroupFieldProps> = ({
  title,
  fieldName,
  options,
  rules,
  variant = 'full',
  onChangeCallback,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const required = typeof rules?.required === 'object';

  return (
    <div
      className={classNames('mt-2', {
        'col-6': variant === 'half',
        'col-12': variant === 'full',
      })}
    >
      <label className="form-label">
        {title}
        {required ? (
          <span aria-label="Required field" className="required">
            *
          </span>
        ) : null}
      </label>
      <Controller
        control={control}
        name={fieldName}
        rules={rules}
        render={({ field: { onChange, onBlur, value } }) => (
          <div>
            {options.map((option) => {
              const checkbox = (
                <StylishNewCheckbox
                  label={option.label}
                  checked={
                    option.disabled
                      ? false
                      : value?.includes(option.value) || false
                  }
                  disabled={option.disabled}
                  onChange={(e) => {
                    if (option.disabled) return;
                    const newValue = e.target.checked
                      ? [...(value || []), option.value]
                      : (value || []).filter((v: string) => v !== option.value);
                    onChange(newValue);
                    onChangeCallback?.(newValue);
                  }}
                  onBlur={onBlur}
                />
              );
              return (
                <div key={option.value} className="mb-1">
                  {option.tooltip ? (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger
                          render={
                            <span className="tw:inline-flex tw:cursor-not-allowed" />
                          }
                        >
                          {checkbox}
                        </TooltipTrigger>
                        <TooltipContent>{option.tooltip}</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    checkbox
                  )}
                </div>
              );
            })}
          </div>
        )}
      />
      {errors[fieldName] ? (
        <div className="form-text form-error">
          {errors[fieldName].message as string}
        </div>
      ) : (
        <div className="form-text-none"></div>
      )}
    </div>
  );
};

export default FormCheckboxGroupField;
